/*
 * wow-home.js — homepage stage interaction (sections/wow-home-stage.liquid).
 * Ported from Claude Design "Lamp and Immersion Test v2", reduced to two lamp photos.
 * No library, no visible strings.
 *
 * Desktop with a hovering pointer (≥768px, hover + fine pointer). Everything drives one value,
 * a crossfade from the lamp's "off" photo to its "on" photo (1 = the "on" photo exactly):
 * - Lamp hover / keyboard focus: "on" → 1, the cabinet dims to 0.37. With data-pulse="true" it "breathes"
 *   (period 1.8s, between 1 and 1 − PULSE_DEPTH); focus is always full, no pulse.
 * - data-proximity="true": approaching the lamp column lights it (up to PROX_MAX) and dims the
 *   cabinet towards 0.37, starting before the lamp's hover area.
 * - Cabinet hover/focus: crossfade closed → open (CSS); the lamp is left exactly as it is.
 * - Leaving fades back in 400ms.
 * - data-reveal="rise" / "centre" / "whole": on hover (or keyboard focus) the lit screen comes on
 *   (RISE_MS, ease-out): "rise" fills from the screen's bottom end up, "centre" from its middle up
 *   and down, "whole" lights the whole screen at once (its opacity, inside the screen only) with
 *   one quick flicker: full, down to FLICKER_LOW, back to full (FLICKER_MS). "ember": the frame
 *   around the veneer is lit at once, and only the veneer inside it glows up in one soft curve
 *   (EMBER_MS, EMBER_EASE: soonest at the start, slow at the end), like a coal catching. Done with a copy of the
 *   "off" photo laid over the veneer only (data-veneer-*: % of the photo) that fades away, so the
 *   frame is never half there. "breath": the same, but the veneer comes on quickly with the frame,
 *   then takes one slow, shallow breath: down to BREATH_LOW and back to full (BREATH_MS).
 *   Leaving runs it back (FALL_MS). Only the screen changes: the lit band stays between
 *   data-screen-top / data-screen-bottom (% of the photo from the top), so the base and the cap keep
 *   the "off" photo. No opacity fade on the lamp in these modes: no pulse, no proximity pre-light;
 *   reduced motion shows the end state at once. data-whole-photo="true" (whole, ember, breath): the
 *   whole lit photo comes on, cap and base included, instead of the screen only.
 * - The second photos (data-src) load after the page, only on such devices.
 * prefers-reduced-motion: no pulse, no fades; the end state is shown directly.
 *
 * Phones (< 768px): the lamp shows its "on" photo only (the brightness wave is CSS). The
 * cabinet photo (data-layer="mobile") loads once the lamp photo has finished, so the lamp
 * gets the bandwidth first.
 */
(() => {
  const root = document.querySelector('[data-wow-home]');
  if (!root) return;

  const desktop = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const usePulse = root.dataset.pulse === 'true';
  const useRise = ['rise', 'centre', 'whole', 'ember', 'breath'].includes(root.dataset.reveal);
  const whole = ['whole', 'ember', 'breath'].includes(root.dataset.reveal);
  const breath = root.dataset.reveal === 'breath';
  const ember = root.dataset.reveal === 'ember' || breath;   // both use the veneer cover
  const fromCentre = root.dataset.reveal === 'centre';
  const wholePhoto = whole && root.dataset.wholePhoto === 'true';
  const useProximity = root.dataset.proximity === 'true';

  const DIM = parseFloat(root.dataset.cabDim) || 0.37;   // cabinet at full proximity / lamp lit (setting, default 37 %)
  const PERIOD = 1.8;          // seconds per breath
  const PULSE_DEPTH = 0.35;    // "on" breathes between 1 and 0.65
  const PROX_MAX = 0.6;        // "on" at the lamp's edge when only approaching
  const RISE_MS = 225;         // the light opening from the centre to the screen's ends
  const FALL_MS = 112;         // and closing again when the pointer leaves
  const EDGE = 1;              // soft edge of the light, % of the photo height
  const FLICKER_MS = 520;      // "whole": on, a quick dip, on again
  const FLICKER_LOW = 0.65;    // how far the dip goes (opacity of the lit screen)
  const EMBER_MS = 2200;       // "ember": the veneer glows up in one soft curve (a little slower than the room glow, owner 2026-10-25)
  const EMBER_EASE = 'cubic-bezier(.3, .45, .3, 1)';
  const BREATH_MS = 1800;      // "breath": on with the frame, one slow dip to BREATH_LOW, back to full
  const BREATH_LOW = 0.85;
  const FRAME_MS = 260;        // "ember": the frame fading in, so off → on is not one jump
  const VENEER = {
    top: parseFloat(root.dataset.veneerTop) || 14.6,       // % of the photo, from the top
    bottom: parseFloat(root.dataset.veneerBottom) || 73.8,
    left: parseFloat(root.dataset.veneerLeft) || 47.9,     // % of the photo, from the left
    right: parseFloat(root.dataset.veneerRight) || 51,
  };
  const SOFT = 0.3;            // soft edge of the veneer cover, %
  let flicker = null;
  // "ember": a copy of the "off" photo over the veneer only, on top of the lit photo
  let cover = null;
  const getCover = () => {
    if (cover || !off || !on) return cover;
    cover = document.createElement('img');
    cover.className = 'wow-home__img wow-home__img--on';
    cover.dataset.layer = 'cover';
    cover.alt = '';
    cover.setAttribute('aria-hidden', 'true');
    cover.decoding = 'async';
    cover.sizes = off.sizes;
    if (off.srcset) cover.srcset = off.srcset;
    cover.src = off.currentSrc || off.src;
    const v = `linear-gradient(to bottom, transparent ${VENEER.top - SOFT}%, #000 ${VENEER.top}%, #000 ${VENEER.bottom}%, transparent ${VENEER.bottom + SOFT}%)`;
    const h = `linear-gradient(to right, transparent ${VENEER.left - SOFT}%, #000 ${VENEER.left}%, #000 ${VENEER.right}%, transparent ${VENEER.right + SOFT}%)`;
    cover.style.maskImage = `${v}, ${h}`;
    cover.style.webkitMaskImage = `${v}, ${h}`;
    cover.style.maskComposite = 'intersect';
    cover.style.webkitMaskComposite = 'source-in';
    cover.style.opacity = '0';
    (on.closest('picture') || on).after(cover);
    return cover;
  };
  // "ember" with the whole photo: a second copy of the "off" photo over everything outside the
  // screen band (cap and base), fading away slowly (BASE_MS) while the frame comes on quickly
  const BASE_MS = parseFloat(root.dataset.baseMs) || EMBER_MS;
  let rest = null;
  const getRest = () => {
    if (rest || !wholePhoto || !cover) return rest;
    rest = cover.cloneNode(false);
    rest.dataset.layer = 'rest';
    const m = `linear-gradient(to bottom, #000 ${SCREEN_TOP - SOFT}%, transparent ${SCREEN_TOP}%, transparent ${SCREEN_BOTTOM}%, #000 ${SCREEN_BOTTOM + SOFT}%)`;
    rest.style.maskImage = m;
    rest.style.webkitMaskImage = m;
    rest.style.maskComposite = '';
    rest.style.webkitMaskComposite = '';
    rest.style.opacity = '0';
    cover.after(rest);
    return rest;
  };
  const SCREEN_TOP = parseFloat(root.dataset.screenTop) || 12;        // % from the photo's top
  const SCREEN_BOTTOM = parseFloat(root.dataset.screenBottom) || 76;
  const CENTRE = 100 - (SCREEN_TOP + SCREEN_BOTTOM) / 2;              // % from the photo's bottom
  const HALF = (SCREEN_BOTTOM - SCREEN_TOP) / 2;
  // Lamp geometry within its photo (fractions): centre x, column top, floor
  const LAMP_CX = 0.5;
  const LAMP_TOP = 0.145;

  const stage = root.querySelector('[data-wow-home-stage]');
  const lampMedia = root.querySelector('[data-wow-home-lamp]');
  const cabMedia = root.querySelector('[data-wow-home-cab]');
  const lampLink = root.querySelector('[data-wow-home-target="lamp"]');
  const cabLink = root.querySelector('[data-wow-home-target="cab"]');
  if (!stage || !lampMedia || !cabMedia || !lampLink || !cabLink) return;
  const lampLabel = lampLink.querySelector('.wow-home__label');
  const cabLabel = cabLink.querySelector('.wow-home__label');
  const on = root.querySelector('[data-layer="on"]');
  const off = root.querySelector('[data-layer="off"]');
  const clamp = (v) => Math.max(0, Math.min(1, v));

  // Fill a deferred photo: its <picture> sources first, then the img itself
  const fill = (img) => {
    const pic = img.closest('picture');
    if (pic) {
      pic.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });
    }
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    if (img.dataset.src) img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  };

  // Phones: the cabinet photo after the lamp photo. Checked at load and again whenever the layout
  // crosses into the phone breakpoint (window resized from desktop width, tablet rotated), so the
  // phone's cabinet photo is never left unloaded. (The lamp's own <picture> sources switch by
  // themselves; the desktop extras have their own listener below.)
  const phone = window.matchMedia('(max-width: 767px)');
  const cabPhone = root.querySelector('[data-layer="mobile"]');
  let cabQueued = false;
  const loadPhoneCab = () => {
    if (!phone.matches || !cabPhone || cabQueued || !cabPhone.hasAttribute('data-src')) return;
    cabQueued = true;
    const loadCab = () => fill(cabPhone);
    // give the browser a frame to re-select the lamp's phone photo after a breakpoint change
    requestAnimationFrame(() => {
      if (!off || (off.complete && off.naturalWidth)) loadCab();
      else {
        off.addEventListener('load', loadCab, { once: true });
        off.addEventListener('error', loadCab, { once: true });
      }
    });
  };
  loadPhoneCab();
  phone.addEventListener('change', loadPhoneCab);

  // Load the second photos after the page, on hover-capable desktops only
  let loaded = false;
  const loadLayers = () => {
    if (loaded || !desktop.matches) return;
    loaded = true;
    root.querySelectorAll('img[data-src]:not([data-layer="mobile"])').forEach(fill);
  };
  const scheduleLoad = () => {
    const go = () => ('requestIdleCallback' in window ? requestIdleCallback(loadLayers, { timeout: 2000 }) : setTimeout(loadLayers, 300));
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  };
  scheduleLoad();
  desktop.addEventListener('change', scheduleLoad);

  // The one value: x = opacity of the "on" photo. A plain crossfade: the "off" photo fades out
  // as "on" fades in (1 − x), so at x = 1 the lamp looks exactly like the "on" photo, even where
  // the photos are semi-transparent (an opaque "off" underneath would show through and brighten).
  let x = 0;
  const paint = (ms, ease) => {
    if (!on) return;
    const t = reduced.matches || !ms ? 'none' : `opacity ${ms}ms ${ease}`;
    on.style.transition = t;
    on.style.opacity = String(clamp(x));
    if (off) {
      off.style.transition = t;
      off.style.opacity = String(1 - clamp(x));
    }
  };

  // The light coming on: a mask on the "on" photo, a band inside the screen (p 0 = dark, 1 = the
  // whole screen lit): from the screen's bottom end up ("rise"), or from its centre both ways
  let p = 0;
  let riseRaf = null;
  const setMask = (v) => {
    if (!on) return;
    p = v;
    const lo = fromCentre ? CENTRE - HALF * v : CENTRE - HALF;
    const hi = fromCentre ? CENTRE + HALF * v : CENTRE - HALF + 2 * HALF * v;
    const m = v <= 0 ? 'linear-gradient(transparent, transparent)' : wholePhoto ? 'none' : `linear-gradient(to top, transparent ${lo - EDGE}%, #000 ${lo}%, #000 ${hi}%, transparent ${hi + EDGE}%)`;
    on.style.maskImage = m;
    on.style.webkitMaskImage = m;
  };
  const stopRise = () => { if (riseRaf) cancelAnimationFrame(riseRaf); riseRaf = null; };
  const riseTo = (target, ms, done) => {
    stopRise();
    const from = p;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // ease-out: fast start, settles at the end
    const step = (now) => {
      const t = ms > 0 ? Math.min(1, (now - t0) / ms) : 1;
      setMask(from + (target - from) * ease(t));
      if (t < 1) riseRaf = requestAnimationFrame(step);
      else { riseRaf = null; if (done) done(); }
    };
    riseRaf = requestAnimationFrame(step);
  };
  // both photos fully shown; the mask alone decides how much of the "on" photo is visible
  const showBoth = () => {
    if (!on) return;
    on.style.transition = 'none';
    on.style.opacity = '1';
    if (off) { off.style.transition = 'none'; off.style.opacity = '1'; }
  };

  let raf = null;
  let phase = 0;
  let last = 0;
  let mode = null; // 'lamp' | 'focus' | 'cab' | null
  let prox = 0;
  const stopLoop = () => { if (raf) cancelAnimationFrame(raf); raf = null; };
  const loop = () => {
    if (raf) return;
    last = performance.now();
    const tick = (now) => {
      if (mode !== 'lamp') { raf = null; return; }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      phase = (phase + dt / PERIOD) % 1;
      const w = 0.5 - 0.5 * Math.cos(2 * Math.PI * phase);
      const goal = 1 - PULSE_DEPTH * w;
      x += (goal - x) * Math.min(1, dt * 3.5);
      paint(0);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  };

  const dims = (lampDim, cabDim) => {
    lampMedia.style.opacity = String(lampDim);
    if (lampLabel) lampLabel.style.opacity = String(lampDim);
    cabMedia.style.opacity = String(cabDim);
    if (cabLabel) cabLabel.style.opacity = String(cabDim);
  };

  const enterLamp = (e) => {
    if (!desktop.matches) return;
    loadLayers();
    const focus = e && e.type === 'focus';
    mode = focus ? 'focus' : 'lamp';
    root.classList.add('is-lamp');
    root.classList.remove('is-cab');
    dims(1, DIM);
    if (useRise && on && whole) {
      // the whole screen at once: the band fixed at full size, only its opacity changes
      stopLoop();
      stopRise();
      setMask(1);
      if (off) { off.style.transition = 'none'; off.style.opacity = '1'; }
      if (flicker) { flicker.cancel(); flicker = null; }
      on.style.transition = 'none';
      if (ember && getCover()) {
        // the lit photo (frame included) at full at once; the cover over the veneer fades away
        const from = parseFloat(getComputedStyle(cover).opacity);
        const onNow = parseFloat(getComputedStyle(on).opacity) || 0;
        const wasOff = onNow < 0.5;
        const r = getRest();
        const restFrom = r ? parseFloat(getComputedStyle(r).opacity) : 0;
        cover.getAnimations().forEach((a) => a.cancel());
        on.getAnimations().forEach((a) => a.cancel());
        if (r) r.getAnimations().forEach((a) => a.cancel());
        cover.style.transition = 'none';
        if (wasOff) cover.style.opacity = '1';
        if (r && wasOff) r.style.opacity = '1';
        on.style.opacity = '1';
        if (reduced.matches || !cover.animate) { cover.style.opacity = '0'; if (r) r.style.opacity = '0'; return; }
        // cap and base: glow up slowly with the veneer instead of switching with the frame
        if (r) { r.style.opacity = '0'; r.animate([{ opacity: wasOff ? 1 : restFrom }, { opacity: 0 }], { duration: BASE_MS, easing: EMBER_EASE }); }
        const start = wasOff ? 1 : from;
        cover.style.opacity = '0';
        // the frame fades in quickly instead of switching on in one step
        if (onNow < 1) on.animate([{ opacity: onNow }, { opacity: 1 }], { duration: FRAME_MS, easing: 'cubic-bezier(.25, .1, .25, 1)' });
        flicker = breath
          // on with the frame, then one slow, shallow breath and back to full
          ? cover.animate([
            { opacity: start, easing: 'cubic-bezier(.25, .1, .25, 1)' },
            { opacity: 0, offset: FRAME_MS / BREATH_MS, easing: 'cubic-bezier(.45, 0, .55, 1)' },
            { opacity: 1 - BREATH_LOW, offset: 0.55, easing: 'cubic-bezier(.45, 0, .55, 1)' },
            { opacity: 0 },
          ], { duration: BREATH_MS })
          // one continuous curve, no step: brightens soonest at the start and settles slowly at the end
          : cover.animate([{ opacity: start }, { opacity: 0 }], { duration: EMBER_MS, easing: EMBER_EASE });
        flicker.onfinish = () => { flicker = null; };
        return;
      }
      if (reduced.matches || !on.animate) { on.style.opacity = '1'; return; }
      const from = parseFloat(getComputedStyle(on).opacity) || 0;
      on.style.opacity = '1';
      flicker = ember
        ? on.animate([{ opacity: from }, { opacity: 1 }], { duration: EMBER_MS, easing: EMBER_EASE })
        : on.animate([
          { opacity: from, easing: 'cubic-bezier(.2, .7, .2, 1)' },
          { opacity: 1, offset: 0.22, easing: 'ease-in' },
          { opacity: FLICKER_LOW, offset: 0.45, easing: 'ease-out' },
          { opacity: 1 },
        ], { duration: FLICKER_MS });
      flicker.onfinish = () => { flicker = null; };
      return;
    }
    if (useRise && on) {
      stopLoop();
      if (p === 0) setMask(0);
      showBoth();
      if (reduced.matches) { stopRise(); setMask(1); }
      else riseTo(1, RISE_MS * (1 - p));
      return;
    }
    if (!focus && usePulse && !reduced.matches) { phase = 0; loop(); }
    else { stopLoop(); x = 1; paint(1000, 'ease-in-out'); }
  };
  const enterCab = () => {
    if (!desktop.matches) return;
    loadLayers();
    mode = 'cab';
    stopLoop();
    root.classList.add('is-cab');
    root.classList.remove('is-lamp');
    // the lamp keeps whatever state it has (no dimming, no switching off); only the cabinet
    // returns to full opacity in case the lamp's proximity had dimmed it
    cabMedia.style.opacity = '1';
    if (cabLabel) cabLabel.style.opacity = '1';
  };
  const leave = () => {
    mode = null;
    stopLoop();
    root.classList.remove('is-lamp', 'is-cab');
    if (useRise && on && ember && cover) {
      // back off: the veneer cover returns, then the lit photo goes at once
      const now = getComputedStyle(cover).opacity;
      if (flicker) { flicker.cancel(); flicker = null; }
      cover.style.opacity = now;
      if (rest) {
        const restNow = parseFloat(getComputedStyle(rest).opacity);
        rest.getAnimations().forEach((a) => a.cancel());
        rest.style.opacity = '1';
        if (!reduced.matches && rest.animate) rest.animate([{ opacity: restNow }, { opacity: 1 }], { duration: FALL_MS, easing: 'ease-out' });
      }
      if (reduced.matches || !cover.animate) { cover.style.opacity = '1'; on.style.opacity = '0'; dims(1, useProximity ? 1 - (1 - DIM) * prox : 1); return; }
      cover.style.opacity = '1';
      flicker = cover.animate([{ opacity: parseFloat(now) }, { opacity: 1 }], { duration: FALL_MS, easing: 'ease-out' });
      // always finish switching off: the pointer may already be on the cabinet (re-entering the lamp
      // cancels this animation, so it never runs then)
      flicker.onfinish = () => { flicker = null; on.getAnimations().forEach((a) => a.cancel()); on.style.opacity = '0'; };
      dims(1, useProximity ? 1 - (1 - DIM) * prox : 1);
      return;
    }
    if (useRise && on && whole) {
      if (flicker) {
        // leaving mid-flicker: continue from where the light is now
        const now = getComputedStyle(on).opacity;
        flicker.cancel();
        flicker = null;
        on.style.transition = 'none';
        on.style.opacity = now;
        void on.offsetWidth;
      }
      on.style.transition = reduced.matches ? 'none' : `opacity ${FALL_MS}ms ease-out`;
      on.style.opacity = '0';
      dims(1, useProximity ? 1 - (1 - DIM) * prox : 1);
      return;
    }
    if (useRise && on) {
      if (reduced.matches) { stopRise(); setMask(0); }
      else riseTo(0, FALL_MS * p);
      dims(1, useProximity ? 1 - (1 - DIM) * prox : 1);
      return;
    }
    x = useProximity && !useRise ? prox * PROX_MAX : 0;
    paint(400, 'ease-out');
    dims(1, useProximity ? 1 - (1 - DIM) * prox : 1);
  };

  lampLink.addEventListener('mouseenter', enterLamp);
  lampLink.addEventListener('focus', enterLamp);
  lampLink.addEventListener('mouseleave', leave);
  lampLink.addEventListener('blur', leave);
  cabLink.addEventListener('mouseenter', enterCab);
  cabLink.addEventListener('focus', enterCab);
  cabLink.addEventListener('mouseleave', leave);
  cabLink.addEventListener('blur', leave);

  if (useProximity) {
    stage.addEventListener('mousemove', (e) => {
      if (!desktop.matches) return;
      const r = lampMedia.getBoundingClientRect();
      if (!r.width) return;
      // distance to the lamp column, in units of the lamp photo's width (as in the design:
      // reach ≈ 0.42 × width, plus an early zone of ≈ 0.59 × width)
      const cx = r.left + LAMP_CX * r.width;
      const yTop = r.top + LAMP_TOP * r.height;
      const yBot = r.bottom;
      const dx = Math.abs(e.clientX - cx);
      const dy = e.clientY < yTop ? yTop - e.clientY : e.clientY > yBot ? e.clientY - yBot : 0;
      prox = clamp(1 - Math.hypot(dx, dy * 0.6) / (r.width * (0.42 + 0.59)));
      if (mode) return;
      loadLayers();
      if (!useRise) {
        x = prox * PROX_MAX;
        paint(0);
      }
      dims(1, 1 - (1 - DIM) * prox);
    });
    stage.addEventListener('mouseleave', () => { prox = 0; if (!mode) { if (!useRise) { x = 0; paint(400, 'ease-out'); } dims(1, 1); } });
  }
})();
